// The manifest's icon renderer, split out of `icons.tsx`.
//
// Its own file for the same reason `icons.tsx` exists: `eslint
// --max-warnings=0` enforces `react-refresh/only-export-components`, and a file
// that exports both a component and a plain constant trips it. `NAV_ICON` and
// `renderModuleIcon` are the two that did.

import type { LucideProps } from "lucide-react";
import { Icon } from "./Icon";
import type { IconName } from "./icons";

/** The 5 nav destinations, mapped to glyph names. Kept beside the set so the nav
 *  and the icon set cannot drift apart. */
export const NAV_ICON: Record<string, IconName> = {
  dashboard: "home",
  calendar: "calendar",
  diary: "book",
  wish: "star",
  more: "more",
};

/**
 * Render a nav manifest entry's icon, whichever set it declares.
 *
 * This is a `.tsx` module rather than living in `lib/moduleManifests.ts` because
 * that file is a `.ts` data module — the first version of this helper put JSX in
 * it and tsc reported eight parse errors, which is the correct outcome and the
 * reason the manifest has stayed render-free for its whole life.
 *
 * One place, because the three consumers (`PillNav` tabs, `PillNavMenu` rows,
 * `IconRail`) would otherwise each grow their own `icon.set === "self" ? … : …`
 * branch. The two-state union is a *data* distinction; the rendering difference
 * is one ternary.
 */
export function renderModuleIcon(
  icon:
    | { set: "self"; name: IconName }
    | { set: "lucide"; component: React.ComponentType<LucideProps> },
  props: { size?: number; className?: string } = {},
) {
  if (icon.set === "self") {
    return <Icon name={icon.name} size={props.size ?? 21} className={props.className} />;
  }
  const Lucide = icon.component;
  return <Lucide size={props.size ?? 21} className={props.className} aria-hidden />;
}
